/**
 * Página Pública para Responder Pesquisas
 * Acessível sem autenticação via link público
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_CONFIG } from '../../services/staticDataConfig';

interface Pesquisa {
  titulo: string;
  descricao?: string;
  mensagem_agradecimento?: string;
  exibe_resultados: boolean;
}

interface Opcao {
  id: number;
  texto: string;
}

interface Pergunta {
  id: number;
  titulo: string;
  descricao?: string;
  tipo: string;
  obrigatoria: boolean;
  config?: {
    min?: number;
    max?: number;
    label_min?: string;
    label_max?: string;
    placeholder?: string;
  };
  opcoes?: Opcao[];
}

export function RespostaPesquisa() {
  const { linkPublico } = useParams<{ linkPublico: string }>();
  
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState('');
  
  const [pesquisa, setPesquisa] = useState<Pesquisa | null>(null);
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [respostas, setRespostas] = useState<Record<number, any>>({});
  const [inicioResposta] = useState(Date.now());

  useEffect(() => {
    carregarPesquisa();
  }, [linkPublico]);

  const carregarPesquisa = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/publico/${linkPublico}`);
      
      if (response.ok) {
        const dados = await response.json();
        setPesquisa(dados.pesquisa);
        setPerguntas(dados.perguntas || []);
      } else {
        const error = await response.json();
        setErro(error.error || 'Pesquisa não encontrada ou não está disponível');
      }
    } catch (error) {
      console.error('Erro ao carregar:', error);
      setErro('Erro ao carregar pesquisa. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const atualizarResposta = (perguntaId: number, valor: any) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  const enviarRespostas = async () => {
    // Validar obrigatórias
    for (const pergunta of perguntas) {
      if (pergunta.obrigatoria) {
        const resposta = respostas[pergunta.id];
        const temResposta = resposta !== undefined && resposta !== '' && 
          !(Array.isArray(resposta) && resposta.length === 0);
        
        if (!temResposta) {
          alert(`Por favor, responda a pergunta: ${pergunta.titulo}`);
          return;
        }
      }
    }

    setEnviando(true);

    try {
      const tempoResposta = Math.floor((Date.now() - inicioResposta) / 1000);

      const dadosEnvio = {
        respostas: perguntas.map(pergunta => {
          const resposta = respostas[pergunta.id];

          return {
            pergunta_id: pergunta.id,
            tipo: pergunta.tipo,
            resposta_texto: ['texto_curto', 'texto_longo', 'email'].includes(pergunta.tipo) ? resposta : undefined,
            resposta_data: pergunta.tipo === 'data' ? resposta : undefined,
            resposta_numero: pergunta.tipo === 'escala_linear' ? resposta : undefined,
            resposta_opcoes: ['multipla_escolha', 'caixas_selecao'].includes(pergunta.tipo) ? 
              (Array.isArray(resposta) ? resposta : (resposta ? [resposta] : [])) : undefined
          };
        }).filter(r => 
          r.resposta_texto || 
          r.resposta_data || 
          r.resposta_numero !== undefined || 
          (r.resposta_opcoes && r.resposta_opcoes.length > 0)
        ),
        tempo_resposta_segundos: tempoResposta
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/publico/${linkPublico}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dadosEnvio)
      });

      if (response.ok) {
        const resultado = await response.json();
        setMensagem(resultado.mensagem || 'Obrigado por responder!');
        setEnviado(true);
      } else {
        const error = await response.json();
        alert(error.error || 'Erro ao enviar respostas');
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      alert('Erro ao enviar respostas. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="text-muted">Carregando pesquisa...</p>
        </div>
      </div>
    );
  }

  // Erro
  if (erro) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-danger mb-3" style={{ fontSize: '4rem' }}></i>
          <h2 className="h4 mb-3">Pesquisa Indisponível</h2>
          <p className="text-muted">{erro}</p>
        </div>
      </div>
    );
  }

  // Enviado com sucesso
  if (enviado) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '500px' }}>
          <div className="card-body text-center p-5">
            <i className="fas fa-check-circle text-success mb-4" style={{ fontSize: '4rem' }}></i>
            <h2 className="h4 mb-3">Resposta Enviada!</h2>
            <p className="text-muted mb-0">{mensagem}</p>
          </div>
        </div>
      </div>
    );
  }

  // Formulário
  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container" style={{ maxWidth: '700px' }}>
        
        {/* Cabeçalho */}
        <div className="card shadow-sm mb-4">
          <div className="card-body" style={{ borderTop: '4px solid #007bff' }}>
            <h1 className="h3 mb-2">{pesquisa?.titulo}</h1>
            {pesquisa?.descricao && (
              <p className="text-muted mb-0">{pesquisa.descricao}</p>
            )}
          </div>
        </div>

        {/* Perguntas */}
        {perguntas.map((pergunta) => (
          <div key={pergunta.id} className="card shadow-sm mb-3">
            <div className="card-body">
              <label className="form-label mb-3">
                <span className="fw-bold">{pergunta.titulo}</span>
                {pergunta.obrigatoria && <span className="text-danger ms-1">*</span>}
              </label>
              {pergunta.descricao && (
                <p className="text-muted small mb-3">{pergunta.descricao}</p>
              )}

              {/* Texto Curto */}
              {pergunta.tipo === 'texto_curto' && (
                <input 
                  type="text"
                  className="form-control"
                  value={respostas[pergunta.id] || ''}
                  onChange={(e) => atualizarResposta(pergunta.id, e.target.value)}
                  placeholder={pergunta.config?.placeholder || 'Sua resposta'}
                />
              )}

              {/* Texto Longo */}
              {pergunta.tipo === 'texto_longo' && (
                <textarea 
                  className="form-control"
                  rows={4}
                  value={respostas[pergunta.id] || ''}
                  onChange={(e) => atualizarResposta(pergunta.id, e.target.value)}
                  placeholder={pergunta.config?.placeholder || 'Sua resposta'}
                />
              )}

              {/* Múltipla Escolha */}
              {pergunta.tipo === 'multipla_escolha' && (
                <div>
                  {pergunta.opcoes?.map(opcao => (
                    <div key={opcao.id} className="form-check mb-2">
                      <input 
                        className="form-check-input"
                        type="radio"
                        name={`pergunta-${pergunta.id}`}
                        id={`opcao-${opcao.id}`}
                        checked={respostas[pergunta.id] === opcao.id}
                        onChange={() => atualizarResposta(pergunta.id, opcao.id)}
                      />
                      <label className="form-check-label" htmlFor={`opcao-${opcao.id}`}>
                        {opcao.texto}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Caixas de Seleção */}
              {pergunta.tipo === 'caixas_selecao' && (
                <div>
                  {pergunta.opcoes?.map(opcao => (
                    <div key={opcao.id} className="form-check mb-2">
                      <input 
                        className="form-check-input"
                        type="checkbox"
                        id={`opcao-${opcao.id}`}
                        checked={(respostas[pergunta.id] || []).includes(opcao.id)}
                        onChange={(e) => {
                          const atual = respostas[pergunta.id] || [];
                          if (e.target.checked) {
                            atualizarResposta(pergunta.id, [...atual, opcao.id]);
                          } else {
                            atualizarResposta(pergunta.id, atual.filter((id: number) => id !== opcao.id));
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor={`opcao-${opcao.id}`}>
                        {opcao.texto}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Escala Linear */}
              {pergunta.tipo === 'escala_linear' && (
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <span className="text-muted small me-2">
                    {pergunta.config?.label_min || pergunta.config?.min || 1}
                  </span>
                  <div className="btn-group flex-wrap" role="group">
                    {Array.from(
                      { length: (pergunta.config?.max || 10) - (pergunta.config?.min || 1) + 1 },
                      (_, i) => (pergunta.config?.min || 1) + i
                    ).map(valor => (
                      <button
                        key={valor}
                        type="button"
                        className={`btn ${respostas[pergunta.id] === valor ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => atualizarResposta(pergunta.id, valor)}
                        style={{ minWidth: '42px' }}
                      >
                        {valor}
                      </button>
                    ))}
                  </div>
                  <span className="text-muted small ms-2">
                    {pergunta.config?.label_max || pergunta.config?.max || 10}
                  </span>
                </div>
              )}

              {/* Data */}
              {pergunta.tipo === 'data' && (
                <input 
                  type="date"
                  className="form-control"
                  style={{ maxWidth: '250px' }}
                  value={respostas[pergunta.id] || ''}
                  onChange={(e) => atualizarResposta(pergunta.id, e.target.value)}
                />
              )}

              {/* Email */}
              {pergunta.tipo === 'email' && (
                <input 
                  type="email"
                  className="form-control"
                  value={respostas[pergunta.id] || ''}
                  onChange={(e) => atualizarResposta(pergunta.id, e.target.value)}
                  placeholder="seu@email.com"
                />
              )}
            </div>
          </div>
        ))}

        {/* Botão Enviar */}
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <span className="text-danger">*</span> Campos obrigatórios
          </small>
          <button 
            className="btn btn-primary btn-lg px-4"
            onClick={enviarRespostas}
            disabled={enviando}
          >
            {enviando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Enviando...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2"></i>
                Enviar
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}

export default RespostaPesquisa;
